import { useEffect, useRef } from "react";
import { C, GRAD, fmtFecha } from "./theme.js";
import { countUp } from "./anime-ui.js";

// ── Piezas premium compartidas por las secciones ──────────────────────

// Tarjeta KPI con número animado (count-up de anime.js).
export function StatCard({ label, value, decimals = 0, prefix = "", suffix = "", displayValue, sub, color = C.brand, icon, center = false }) {
  const ref = useRef(null);
  const numeric = value != null && !isNaN(value);
  useEffect(() => {
    if (numeric && ref.current) countUp(ref.current, Number(value), { decimals, prefix, suffix, duration: 1300 });
  }, [value, numeric, decimals, prefix, suffix]);

  return (
    <div className="fg-card fg-kpi" style={{
      flex: "1 1 165px", minWidth: 155, background: "#fff", border: `1px solid ${C.line}`,
      borderRadius: 14, padding: "9px 12px", position: "relative", overflow: "hidden",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4, justifyContent: center ? "center" : "flex-start" }}>
        <div style={{
          width: 26, height: 26, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
          background: `${color}14`, color, flexShrink: 0,
        }}>{icon}</div>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5, color: C.muted, textTransform: "uppercase", lineHeight: 1.25 }}>{label}</div>
      </div>
      <div style={{ fontSize: center ? 21 : 20, fontWeight: 800, lineHeight: 1.05, color: C.ink, textAlign: center ? "center" : "left" }}>
        {numeric ? <span ref={ref}>{prefix}0{suffix}</span> : (displayValue ?? "—")}
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 2, minHeight: 14, textAlign: center ? "center" : "left" }}>{sub}</div>
      <div style={{ position: "absolute", right: -18, bottom: -18, width: 74, height: 74, borderRadius: "50%", background: `${color}0d` }} />
    </div>
  );
}

// Icono SVG genérico para StatCard.
export function Ic({ d, box = "0 0 24 24" }) {
  return (
    <svg viewBox={box} width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {typeof d === "string" ? <path d={d} /> : d}
    </svg>
  );
}

// Bloque "en espera" premium con pasos de activación.
export function WaitCard({ icon = "🛰️", title, hint, steps = [], pill, compact = false, fill = false }) {
  return (
    <div style={{
      padding: compact ? "18px 6px" : "30px 20px", textAlign: "center",
      ...(fill ? { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" } : null),
    }}>
      <div style={{ fontSize: compact ? 26 : 34, marginBottom: 8, opacity: 0.9 }}>{icon}</div>
      <div style={{ fontSize: compact ? 13.5 : 15.5, fontWeight: 800, color: C.ink, marginBottom: 5 }}>{title}</div>
      {pill && (
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, color: C.brand, background: `${C.brand}12`, border: `1px solid ${C.brand}33`, borderRadius: 999, padding: "3px 11px" }}>{pill}</span>
        </div>
      )}
      {hint && <div style={{ fontSize: 12.5, color: C.muted, maxWidth: 480, margin: "0 auto 14px", lineHeight: 1.55 }}>{hint}</div>}
      {steps.length > 0 && (
        <div style={{ display: "inline-block", textAlign: "left", background: "#f8fafc", border: `1px solid ${C.line}`, borderRadius: 12, padding: "13px 18px" }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: i === steps.length - 1 ? 0 : 8 }}>
              <span style={{
                width: 18, height: 18, borderRadius: "50%", background: GRAD, color: "#fff",
                fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1,
              }}>{i + 1}</span>
              <span style={{ fontSize: 12, color: C.page, lineHeight: 1.45 }}>{s}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Tira de estado de las fuentes de datos (Dashboard). Cada chip enlaza al
// perfil/consola de la fuente. El color del chip ES el estado: verde=conectada
// con datos · ámbar=conectada esperando datos · gris=sin conectar.
export function SourceStrip({ fuentes }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <style>{`.fg-source:hover{box-shadow:0 5px 14px rgba(0,81,135,.14);transform:translateY(-1px)}`}</style>
      {fuentes.map((f) => {
        const estado = f.ok ? "ok" : f.ready ? "ready" : "off";
        const dot = estado === "ok" ? C.verde : estado === "ready" ? C.naranja : C.gris;
        const fondo = estado === "ok" ? `${C.verde}14` : estado === "ready" ? `${C.naranja}14` : "#fff";
        const borde = estado === "ok" ? `${C.verde}55` : estado === "ready" ? `${C.naranja}55` : C.line;
        const detalle = f.ok ? `Conectada · último dato: ${fmtFecha(f.last)}` : estado === "ready" ? (f.nota || "Conectada — esperando los primeros datos") : (f.nota || "Aún sin conectar");
        return (
          <a key={f.id} href={f.url} target="_blank" rel="noreferrer" className="fg-source"
            title={`${detalle} — clic para abrir ${f.label}`} style={{
              display: "inline-flex", alignItems: "center", gap: 7, fontSize: 11, fontWeight: 700,
              color: estado === "off" ? C.muted : C.page, background: fondo, border: `1px solid ${borde}`,
              borderRadius: 999, padding: "5px 12px", textDecoration: "none", cursor: "pointer",
              transition: "box-shadow .18s ease, transform .18s ease",
            }}>
            <span className={estado === "off" ? "fg-breath" : ""} style={{ width: 7, height: 7, borderRadius: "50%", background: dot }} />
            {f.label}
            {f.ok && f.last && <span style={{ color: C.faint, fontWeight: 600 }}>· {fmtFecha(f.last)}</span>}
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
              strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5, flexShrink: 0 }}>
              <path d="M7 17 17 7" /><path d="M9 7h8v8" />
            </svg>
          </a>
        );
      })}
    </div>
  );
}

// Cabecera de sección dentro del área principal.
export function SectionHeader({ title, subtitle, right }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, flexWrap: "wrap", marginBottom: 12 }}>
      <div>
        <h1 style={{ fontSize: 19, fontWeight: 800, margin: 0, color: C.ink, letterSpacing: -0.2 }}>{title}</h1>
        {subtitle && <div style={{ fontSize: 12.5, color: C.muted, marginTop: 3 }}>{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}
